import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios, { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllLogicsSingleResponse,
  IGetAllTaskTemplatesSingleResponse,
  ILogicPostRequest,
  ITaskInputComponentsResponse,
  ITaskTemplatesPatchRequest,
  ITaskTemplatesPostRequest,
  ITaskTemplatesSinleResponse,
  IWorkUnitAmountsPostRequest,
} from 'types/api/tasksTemplatesApi.interface'
import {
  ITaskTemplatesSlice,
  ITaskTemplateState,
  ITaskTemplateStateComponent,
  ITaskTemplateWComponent,
  ITaskTemplateWorkUnitAmount,
  IToolTypeComponent,
} from 'types/task-templates.interface'
import { baseUrl } from 'utils/axios'
import { dataURItoBlob, handleErrors, formatParams } from 'utils/helpers'
import { v4 } from 'uuid'

const initialState: ITaskTemplatesSlice = {
  isLoading: false,
  tasksLogics: [],
  createdId: null,
  singleTaskTemplate: null,
  allTaskTemplates: [],
  totalPages: 0,
  totalCount: 0,
  noTaskTemplates: false,
  inputComponents: null,
}

export const getTasksTemplateLogics = createAsyncThunk(
  'taskTemplates/getTasksLogics',
  async (ai: boolean, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetAllLogicsSingleResponse[]> = await baseUrl.get(
        ai ? 'task/template/logics/ai' : 'task/template/logics',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInputComponents = createAsyncThunk(
  'taskTemplates/getInputComponents',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<ITaskInputComponentsResponse> = await baseUrl.get(
        'task/template/' + id + '/input-components',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getToolComponents = createAsyncThunk(
  'taskTemplates/getToolComponents',
  async (_, thunkAPI) => {
    try {
      const resp = await baseUrl.get('task/template/logics')
      const newObject = resp.data.map((item) => {
        item.input = item.input.map((i) => ({ ...i, id: v4() }))
        item.output = item.output.map((o) => ({ ...o, id: v4() }))
        return item
      })
      return newObject
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addTaskTemplate = createAsyncThunk(
  'taskTemplates/addTask',
  async (task: ITaskTemplateState, thunkAPI) => {
    try {
      const { image } = task
      const forSubmit: ITaskTemplatesPostRequest = {
        operationId: task.operation?.id,
        requiredCapabilityId: task.requiredCapability?.value,
        requiredMachineCategoryId: task.requiredMachineCategory?.value,
        fallbackRoleIds: task.fallBackRoles.map((r) => r.id),
        allowedRoleIds: task.allowedRoles.map((r) => r.id),
        requiredMachineModelFieldValues: task.requiredMachineModelFieldValues.map((v) => ({
          fieldId: v.id,
          value: v.value,
        })),
        operationParameterValues: task.operationParameterValues.map((v) => ({
          defaultValue: v.defaultValue,
          operationParameterId: v.operationParameter?.id,
        })),
        toolTypeComponents: task.toolTypeComponents.map((t: IToolTypeComponent) => ({
          toolTypeComponentId: t.component.value,
          name: t.name,
          unitId: t.component.label == 'Item' ? t.unit.value : null,
        })),
        workerComponentFieldIds: task.workerComponents.map((c: ITaskTemplateWComponent) => c.id),
        components: task.components,
        shouldUseLocationTracking: task.shouldUseLocationTracking,
      }

      const resp = await baseUrl.post('task/template', forSubmit)
      if (image) {
        const blobedImage = dataURItoBlob(image)
        try {
          await axios.put(resp.data.imagePresignedUploadUrl, blobedImage)
          return resp.data
        } catch (error) {
          console.log(error)
          return thunkAPI.rejectWithValue(error)
        }
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const duplicateTaskTemplate = createAsyncThunk(
  'taskTemplates/duplicateTask',
  async (nameId: { name: string; taskTemplateId: number }, thunkAPI) => {
    try {
      const resp = await baseUrl.post('task/template/duplicate', nameId)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getTaskTemplates = createAsyncThunk(
  'taskTemplates/getTasks',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllTaskTemplatesSingleResponse>> =
        await baseUrl.get('task/template/overviews', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editTaskTemplate = createAsyncThunk(
  'taskTemplates/editTask',
  async (task: ITaskTemplateState, thunkAPI) => {
    // § Proveriti da li task koji stize je isti kao interface EDIT: Nije, komponente
    try {
      const { id, image } = task
      const forSubmit: ITaskTemplatesPostRequest = {
        operationId: task.operation?.id,
        requiredCapabilityId: task.requiredCapability?.value,
        requiredMachineCategoryId: task.requiredMachineCategory?.value,
        fallbackRoleIds: task.fallBackRoles.map((r) => r.id),
        allowedRoleIds: task.allowedRoles.map((r) => r.id),
        requiredMachineModelFieldValues: task.requiredMachineModelFieldValues.map((v) => ({
          fieldId: v.id,
          value: v.value,
        })),
        operationParameterValues: task.operationParameterValues.map((v) => ({
          defaultValue: v.defaultValue,
          operationParameterId: v.operationParameter?.id,
        })),
        toolTypeComponents: task.toolTypeComponents.map((t: IToolTypeComponent) => ({
          toolTypeComponentId: t.component.value,
          name: t.name,
          unitId: t.component.label == 'Item' ? t.unit.value : null,
        })),
        workerComponentFieldIds: task.workerComponents.map((c: ITaskTemplateWComponent) => c.id),
        components: task.components,
        shouldUseLocationTracking: task.shouldUseLocationTracking,
      }
      const resp = await baseUrl.patch('task/template/' + id, forSubmit)
      if (image) {
        const blobedImage = dataURItoBlob(image)
        try {
          await axios.put(resp.data.imagePresignedUploadUrl, blobedImage)
          return resp.data
        } catch (error) {
          console.log(error)
          return thunkAPI.rejectWithValue(error)
        }
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addLogic = createAsyncThunk(
  'taskTemplates/addLogic',
  async (
    logic: { id: string; components: ITaskTemplateStateComponent[]; functionType: number }, // ispitati
    thunkAPI,
  ) => {
    try {
      const { id, components, functionType } = logic
      const forDispatch: ILogicPostRequest = {
        componentLogics: components.map((c) => ({
          functionType,
          calculatedTaskComponentId: c.id,
          functionId: c.functionId,
          functionResultIndex: c.functionResultIndex,
          inputParameters: c.inputParameters || [],
        })),
      }
      const resp = await baseUrl.post('task/template/' + id + '/logic', forDispatch)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addWorkUnitAmounts = createAsyncThunk(
  'taskTemplates/addWorkUnitAmounts',
  async (workUnitsAmounts: IWorkUnitAmountsPostRequest, thunkAPI) => {
    try {
      const resp = await baseUrl.post('task/template/work-unit-amount-per-ranges', workUnitsAmounts)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleTaskTemplate = createAsyncThunk(
  'taskTemplates/geSingleTask',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ITaskTemplatesSinleResponse> = await baseUrl.get(
        'task/template/' + id,
      )
      const taskObejct: ITaskTemplateState = {
        functionType: resp.data.components.find((c) => c.functionType)?.functionType,
        image: null,
        imageRaw: null,
        logic: [],
        name: resp.data.name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // TODO
        components: resp.data.components,
        workerComponents: resp.data.workerComponents.map((wc) => ({
          id: wc.field.id,
          name: wc.field.name,
        })),
        workUnitAmounts: resp.data.workUnitAmounts.map(
          (item: ITaskTemplateWorkUnitAmount, index: number) => ({
            ...item,
            id: index,
          }),
        ),
        toolTypeComponents: resp.data.toolTypeComponents.map((i) => ({
          component: {
            label: i.toolTypeComponent.name,
            value: i.toolTypeComponent.id,
          },
          name: i.name,
          unit: i.unit
            ? {
                label: i.unit.name,
                value: i.unit.id,
              }
            : null,
        })),
        shouldUseLocationTracking: resp.data.shouldUseLocationTracking,
        // operation & params
        operation: resp.data.operation
          ? {
              id: resp.data.operation.operationId,
              name: resp.data.operation.operationName,
            }
          : null,
        operationParameterValues: resp.data.operation
          ? resp.data.operation.operationParameterValues.map((p) => ({
              operationParameter: {
                id: p.operationParameterId,
                name: p.parameterName,
              },
              defaultValue: p.defaultValue,
            }))
          : [],
        // capability & roles
        requiredCapability: resp.data.requiredCapability
          ? {
              value: resp.data.requiredCapability.id,
              label: resp.data.requiredCapability.name,
            }
          : null,
        allowedRoles: resp.data.allowedRoles?.map((r) => r.role) || [],
        fallBackRoles: resp.data.fallbackRoles?.map((r) => r.role) || [],
        // machine fields
        requiredMachineCategory: resp.data.requiredMachineCategory
          ? {
              value: resp.data.requiredMachineCategory.id,
              label: resp.data.requiredMachineCategory.name,
            }
          : null,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        requiredMachineModelFieldValues: resp.data.requiredMachineModelFieldValues?.map((v) => ({
          field: v.field,
          id: v.id,
          value: v.valueString ?? v.valueNumber,
        })),
        selectedMachineModel: null,
        // operation group / type (best-effort mapping)
        operationTypeId: resp.data.operation?.operationGroupId || null,
        operationTypeParameterValues: [],
        selectedOperation: resp.data.operation
          ? {
              id: resp.data.operation.operationGroupId,
              name: resp.data.operation.operationGroupName,
              operations: [
                {
                  id: resp.data.operation.operationId,
                  name: resp.data.operation.operationName,
                  parameters: resp.data.operation.parameters.map((p) => ({
                    id: p.id,
                    name: p.name,
                    parameterId: p.parameterId,
                    unit: p.unit,
                    unitId: p.unitId,
                  })),
                },
              ],
            }
          : null,
      }

      return taskObejct
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteTaskTemplate = createAsyncThunk(
  'taskTemplates/deleteTask',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('task/template/' + id, thunkAPI)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSimpleTaskTemplates = createAsyncThunk(
  'taskTemplates/getSimpleTasks',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllTaskTemplatesSingleResponse>> =
        await baseUrl.get('/task/templates/simple', thunkAPI)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
const taskTemplatesSlice = createSlice({
  name: 'task-templates',
  initialState,
  reducers: {
    clearTaskTemplate: (state) => {
      state.createdId = null
      state.singleTaskTemplate = null
      state.tasksLogics = []
    },
    clearInputComponents: (state) => {
      state.inputComponents = null
    },
    clearTaskTemplatesList: (state) => {
      state.allTaskTemplates = []
      state.totalCount = 0
      state.totalPages = 0
    },
  },
  extraReducers: {
    [getTasksTemplateLogics.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTasksTemplateLogics.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.tasksLogics = payload
      state.noTaskTemplates = state.allTaskTemplates.length < 1
    },
    [getTasksTemplateLogics.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addTaskTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [addTaskTemplate.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.createdId = payload.id
      state.noTaskTemplates = false
      toast.success('Task template successfully created')
    },
    [addTaskTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editTaskTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [editTaskTemplate.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleTaskTemplate.components = payload.components
      toast.success('Task template updated successfully')
    },
    [editTaskTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addLogic.pending.type]: (state) => {
      state.isLoading = true
    },
    [addLogic.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Logic successfully saved')
    },
    [addLogic.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [duplicateTaskTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [duplicateTaskTemplate.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      toast.success('Task template successfully duplicated')
      if (state.allTaskTemplates.length < 5) {
        state.allTaskTemplates.push(payload)
      }
    },
    [duplicateTaskTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addWorkUnitAmounts.pending.type]: (state) => {
      state.isLoading = true
    },

    [addWorkUnitAmounts.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleTaskTemplate.workUnitAmounts = payload
      toast.success('Work unit amounts successfully saved')
    },
    [addWorkUnitAmounts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleTaskTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleTaskTemplate.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleTaskTemplate = payload
    },
    [getSingleTaskTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getTaskTemplates.pending.type]: (state) => {
      state.isLoading = true
    },
    [getTaskTemplates.fulfilled.type]: (
      state: ITaskTemplatesSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllTaskTemplatesSingleResponse> },
    ) => {
      state.isLoading = false
      state.allTaskTemplates = payload.data
      state.noTaskTemplates = payload.data.length < 1
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getTaskTemplates.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteTaskTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteTaskTemplate.fulfilled.type]: (state: ITaskTemplatesSlice, { payload }) => {
      state.isLoading = false
      toast.success('Task template deleted')
      state.allTaskTemplates = state.allTaskTemplates.filter((item) => item.id !== payload)
    },
    [deleteTaskTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSimpleTaskTemplates.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSimpleTaskTemplates.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.allTaskTemplates = payload
    },
    [getSimpleTaskTemplates.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getInputComponents.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInputComponents.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.inputComponents = payload
    },
    [getInputComponents.rejected.type]: (state) => {
      state.isLoading = false
    },
  },
})

export const { clearTaskTemplate, clearTaskTemplatesList, clearInputComponents } =
  taskTemplatesSlice.actions
export default taskTemplatesSlice.reducer
